import { useEffect, useState } from "react";
import { useClerk } from "@clerk/react";
import { toast } from "sonner";
import { useApi, errMsg } from "@/lib/api";
import { t } from "@/i18n";

export default function Onboarding({ me, onDone }) {
  const api = useApi();
  const { signOut } = useClerk();
  const newOrg = !me.org;
  const [org, setOrg] = useState("");
  const [nick, setNick] = useState(me.nickname || "");
  const [busy, setBusy] = useState(false);
  const [teams, setTeams] = useState([]);
  const [join, setJoin] = useState("");
  const [role, setRole] = useState("");
  const [title, setTitle] = useState("");
  const [roles, setRoles] = useState([]);
  const [draft, setDraft] = useState("");
  const addRole = () => {
    const r = draft.trim().replace(/\s+/g, " ").slice(0, 30);
    if (r && !roles.some((x) => x.toLowerCase() === r.toLowerCase())) setRoles([...roles, r]);
    setDraft("");
  };
  useEffect(() => { if (newOrg) api.get("/public/open-teams").then((r) => setTeams(r.data)).catch(() => {}); }, [api, newOrg]);
  const team = teams.find((x) => x.slug === join);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/onboarding", { nickname: nick, org_name: join ? "" : org, join_org: join, join_role: join ? role : "", job_title: join ? "" : title,
        roles: join ? [] : [...roles, ...(draft.trim() ? [draft.trim()] : [])] });
      onDone();
    } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="onboarding-page">
      <form onSubmit={submit} className="w-full max-w-md stack rise">
        <img src="/mascot.png" alt="" className="h-16 w-auto self-start" />
        <div>
          <h1 className="text-4xl font-bold tracking-tight">{t(newOrg ? "Set up your team" : "Welcome to {org}").replace("{org}", me.org?.name || "")}</h1>
          <p className="muted mt-2">{t(newOrg ? "You'll be the admin. Invite your team once you're in." : "Pick the nickname your team will see.")}</p>
        </div>
        {newOrg && teams.length > 0 && (
          <div className="stack-sm" data-testid="onboarding-join">
            <span className="mono-label">{t("Join a demo team instead")}</span>
            {teams.map((x) => (
              <label key={x.slug} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" name="team" checked={join === x.slug} onChange={() => { setJoin(x.slug); setRole(x.roles[0]); }} data-testid={`onboarding-join-${x.slug}`} /> {x.name}
              </label>
            ))}
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="radio" name="team" checked={!join} onChange={() => setJoin("")} data-testid="onboarding-join-none" /> {t("No, set up my own team")}</label>
            {team && <select className="field" value={role} onChange={(e) => setRole(e.target.value)} data-testid="onboarding-join-role">
              {team.roles.map((r) => <option key={r} value={r}>{t(r)}</option>)}
            </select>}
          </div>
        )}
        {newOrg && !join && (
          <label className="stack-sm">
            <span className="mono-label">{t("Organization name")}</span>
            <input className="field" value={org} onChange={(e) => setOrg(e.target.value)} placeholder={t("e.g. Sunny Side Coffee")} required minLength={2} maxLength={80} data-testid="onboarding-org-input" />
          </label>
        )}
        {newOrg && !join && (
          <>
            <label className="stack-sm">
              <span className="mono-label">{t("Your job title")}</span>
              <input className="field" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("e.g. Owner")} maxLength={30} data-testid="onboarding-job-title-input" />
            </label>
            <div className="stack-sm" data-testid="onboarding-roles">
              <span className="mono-label">{t("Team roles (optional)")}</span>
              {roles.length > 0 && <div className="flex flex-wrap gap-1.5">{roles.map((r) => (
                <span key={r} className="chip chip-info" data-testid={`onboarding-role-chip-${r.toLowerCase().replace(/\s+/g, "-")}`}>{r}
                  <button type="button" aria-label={t("Remove")} className="ml-1 opacity-70 hover:opacity-100" onClick={() => setRoles(roles.filter((x) => x !== r))} data-testid={`onboarding-role-remove-${r.toLowerCase().replace(/\s+/g, "-")}`}>×</button>
                </span>))}</div>}
              <div className="flex gap-2">
                <input className="field" value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={30} placeholder={t("e.g. Barista, Cashier, Cook")}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addRole(); } }} data-testid="onboarding-role-input" />
                <button type="button" className="btn btn-ghost" onClick={addRole} disabled={!draft.trim()} data-testid="onboarding-role-add">{t("Add")}</button>
              </div>
              <p className="text-xs faint">{t("Staff roles see Team OI. You can change roles and access later in Team settings.")}</p>
            </div>
          </>
        )}
        <label className="stack-sm">
          <span className="mono-label">{t("Nickname")}</span>
          <input className="field" value={nick} onChange={(e) => setNick(e.target.value)} placeholder={t("e.g. Jenny")} required maxLength={30} data-testid="onboarding-nickname-input" />
        </label>
        <div className="flex items-center gap-3">
          <button className="btn btn-primary" disabled={busy} data-testid="onboarding-submit">{t(busy ? "Saving…" : "Continue")}</button>
          <button type="button" className="btn btn-ghost" onClick={() => signOut({ redirectUrl: "/" })} data-testid="onboarding-sign-out">{t("Use another account")}</button>
        </div>
      </form>
    </div>
  );
}
